import { lazy, Suspense, useEffect, useMemo } from 'react';
import { useLoaderData, useSearchParams } from '@remix-run/react';
import { LoaderArgs, json, redirect } from '@shopify/remix-oxygen';
import { Routes } from '~/common/constants/routes';
import { isScratchDropEnabled } from '~/lib/scratchDrop.server';
import { isShopHomepage, loadShop } from '~/lib/shop.server';
import {
  convertProducts,
  convertProductsToMysteryProducts,
} from '~/graphql/converters/product';
import { ProductsList } from '~/sections/shop/ProductsSection';
import {
  EU_PRODUCT_ORDER,
  SHOP_PRODUCT_ORDER,
} from '~/common/constants/product';
import { Product } from '~/types/product';
import PageWrapper from '~/layout/components/pageWrapper/PageWrapper';
import PasswordForm from '~/sections/shop/PasswordForm';
import { useAppSSR } from '~/recoil/useAppSSR';
import { isBrowser } from '~/common/utils/browser';
import styled from 'styled-components';
import useColor from '~/recoil/useColor';
import { colors } from '~/theme/colors';
import { Image } from '~/components/wrappers/Image';
import BlackWeekBanner from 'public/assets/images/BANNER_BLACKWEEK.png';
import { BRAND, SEO, FALLBACK_SITE_URL } from '~/common/constants/seo';
import EmptyProductsState from '~/components/EmptyProductsState';
import {
  ShopFilters,
  FILTER_PARAM,
  resolveActiveFilter,
} from '~/components/ShopFilters';
import { backdropLayer } from '~/components/gateBackdrop';
import {
  CAMO_GATE_BACKDROP,
  SIGNUP_BACKDROP,
} from '~/common/constants/gateBackdrop';
import { VisuallyHiddenH1 } from '~/components/typography/VisuallyHiddenH1';
import { PageCountdown } from '~/components/countdown/PageCountdown';
// Same card as `/signup`. No `links` camo preload here, unlike that route: Remix `links`
// is static per route, so it would download ~220KB for every product-grid visitor.
import SignUpCard from '~/sections/signup/SignUpCard';
const FloatingProductList = lazy(() =>
  import('~/sections/shop/FloatingProductList').then((mod) => ({
    default: mod.FloatingProductList,
  })),
);

export async function loader(args: LoaderArgs) {
  const { env } = args.context;
  /**
   * A scratch drop owns the homepage and moves the catalogue there, so `/shop` sends
   * shoppers back — the same effect `PUBLIC_ENABLE_FLASH_SALE` has via
   * `app/layout/index.tsx`, but done here rather than copied from there. `SHOP_AS_HOMEPAGE`
   * does the same, permanently-until-cleared; the query string rides along so `?filter=`
   * links still land on a filtered grid.
   *
   * The flash sale redirects client-side, from a `useEffect` calling `navigate`, which means
   * this loader still runs, still queries Shopify for up to 250 products and still ships the
   * whole page before the browser bounces it. A loader redirect costs one 302 and no query
   * at all, and it works with JavaScript disabled.
   *
   * Deliberately before the password gate below: which of the two answers first is not
   * observable — both send the visitor away from the catalogue — and this way an enabled
   * drop cannot be probed for whether `/shop` happens to be gated.
   */
  if (isScratchDropEnabled(env) || isShopHomepage(env)) {
    return redirect(`${Routes.Home}${new URL(args.request.url).search}`);
  }

  return json(await loadShop(args));
}

export const meta = ({ data }: { data: any }) => {
  const siteUrl = data?.siteUrl || FALLBACK_SITE_URL;
  const pageUrl = `${siteUrl}/shop`;
  const title = SEO.defaultTitle;
  const description = SEO.defaultDescription;

  // ItemList structured data for product listing page
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description: description,
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: BRAND.name,
      url: siteUrl,
    },
    about: {
      '@type': 'Thing',
      name: 'Sustainable Streetwear',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Shop', item: pageUrl },
    ],
  };

  return [
    { title },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: pageUrl },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: `${siteUrl}${SEO.defaultOgImage}` },
    { property: 'og:image:width', content: String(SEO.defaultOgImageWidth) },
    { property: 'og:image:height', content: String(SEO.defaultOgImageHeight) },
    { property: 'og:image:alt', content: 'Broken Planet Shop' },
    { property: 'og:site_name', content: SEO.brandName },
    { name: 'twitter:card', content: SEO.twitterCard },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: `${siteUrl}${SEO.defaultOgImage}` },
    { name: 'referrer', content: 'strict-origin-when-cross-origin' },
    {
      'script:ld+json': JSON.stringify([itemListSchema, breadcrumbSchema]),
    },
  ];
};

const Products = () => {
  const data = useLoaderData();

  const [{ isAccessible }, setIsAccessible] = useAppSSR();

  const SALE_TITLE =
    typeof window === 'undefined' ? null : window?.ENV?.PUBLIC_SALE_TITLE;

  const products: Array<Product> = useMemo(() => {
    if (typeof window === 'undefined') return [];

    const MYSTERY_COLLECTION_NAME = window?.ENV?.PUBLIC_MYSTERY_COLLECTION_NAME;

    if (isBrowser && MYSTERY_COLLECTION_NAME === '') {
      return data.filteredProducts
        ? convertProducts(data.filteredProducts.edges)
        : [];
    }

    return data.filteredProducts && data.mysteryCollections.length > 0
      ? convertProductsToMysteryProducts(
          data.filteredProducts,
          data.mysteryCollections,
          MYSTERY_COLLECTION_NAME,
        )
      : [];
  }, [data.filteredProducts, data.mysteryCollections]);

  const isFloatingFlashSale =
    isBrowser && window?.ENV?.PUBLIC_ENABLE_FLOATING_FLASH_SALE === 'true';

  const productFiltersEnabled = Boolean(data.productFiltersEnabled);

  const [searchParams] = useSearchParams();
  const excludedFilters = useMemo(
    () => data.excludedFilters ?? [],
    [data.excludedFilters],
  );
  const activeFilter = useMemo(() => {
    if (!productFiltersEnabled) return undefined;
    return resolveActiveFilter(
      searchParams.get(FILTER_PARAM),
      products,
      excludedFilters,
    );
  }, [productFiltersEnabled, searchParams, products, excludedFilters]);

  const { setColor } = useColor();

  useEffect(() => {
    setColor(colors.colorMain);
  }, []);

  if (!isAccessible) {
    return (
      <>
        <VisuallyHiddenH1>Shop Broken Planet Streetwear</VisuallyHiddenH1>
        <PasswordForm setIsAccessible={setIsAccessible} />
      </>
    );
  }

  // Decided in the loader rather than from `window.ENV`, so the server renders the same
  // branch as the client. No `VisuallyHiddenH1`: `CampaignScreen` renders the h1.
  if (data.isSignUpSurface) {
    return (
      <SignUpCard
        title={data.title}
        descriptionLines={data.descriptionLines}
        subtitle={data.subtitle}
        countdownTitle={data.countdownTitle}
        countdownAt={data.countdownAt}
        backdrop={SIGNUP_BACKDROP}
      />
    );
  }

  const activeRegion =
    isBrowser && window?.ENV?.RESOLVED_REGION
      ? window.ENV.RESOLVED_REGION
      : undefined;

  if (products.length === 0) {
    return (
      <PageWrapper
        paddingTop={4.5}
        paddingTopMobile={4.5}
        paddingX={0}
        paddingXMobile={0}
        paddingBottom={2.5}
        paddingBottomMobile={1.5}
        maxWidth="unset"
      >
        <VisuallyHiddenH1>Shop Broken Planet Streetwear</VisuallyHiddenH1>
        <EmptyProductsState region={activeRegion} />
      </PageWrapper>
    );
  }

  const grid = (
    <PageWrapper
      paddingTop={4.5}
      paddingTopMobile={4.5}
      paddingX={0}
      paddingXMobile={0}
      paddingBottom={2.5}
      paddingBottomMobile={1.5}
      maxWidth="unset"
    >
      <VisuallyHiddenH1>Shop Broken Planet Streetwear</VisuallyHiddenH1>
      {SALE_TITLE && (
        <StyledSaleImageWrapper>
          <Image
            src={BlackWeekBanner}
            alt="Broken Planet Black Week Sale - Limited time offers on sustainable streetwear"
          />
        </StyledSaleImageWrapper>
      )}
      {isFloatingFlashSale ? (
        <Suspense fallback={null}>
          <FloatingProductList products={products} />
        </Suspense>
      ) : (
        <>
          {/* `COUNTDOWN_*`, non-null here only while the grid is the homepage. */}
          <PageCountdown title={data.countdownTitle} at={data.countdownAt} />
          {productFiltersEnabled && (
            <ShopFilters
              products={products}
              excludedFilters={excludedFilters}
              hasActiveCountdownTimer={data.countdownAt !== null}
              hasActiveCountdownTitle={data.countdownTitle !== null}
            />
          )}
          <ProductsList
            products={products}
            activeFilter={activeFilter}
            productOrder={
              data.isEuMarket ? EU_PRODUCT_ORDER : SHOP_PRODUCT_ORDER
            }
          />
        </>
      )}
    </PageWrapper>
  );

  return data.hasCamoBackground ? <CamoBackdrop>{grid}</CamoBackdrop> : grid;
};

/**
 * The `/signup` camo behind the product grid, on while `SHOP_CAMO_BACKGROUND=true`.
 *
 * Painted by a `position: fixed` pseudo-element rather than on the grid itself: the grid
 * is many viewports tall, and `background-size: cover` on it would scale the artwork to
 * the page height (a blurry blow-up), while `background-attachment: fixed` is ignored on
 * iOS. `isolation: isolate` keeps the `z-index: -1` layer inside this element's stacking
 * context, so it sits behind the products but above whatever paints the page background.
 * Same scrim as every other camo surface — see `backdropLayer`.
 */
const CamoBackdrop = styled.div`
  position: relative;
  isolation: isolate;
  min-height: 100svh;

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: -1;
    ${backdropLayer(CAMO_GATE_BACKDROP)}
  }
`;

export default Products;

const StyledSaleImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  transform: translateY(calc(-7.5rem - 20px));

  > img {
    width: 100%;
    max-width: 720px;
  }

  @media ${({ theme }) => theme.breakpoints.lg} {
    transform: translateY(calc(-1rem - 40px));

    > img {
      width: 100%;
      max-width: 620px;
    }
  }

  @media ${({ theme }) => theme.breakpoints.md} {
    transform: translateY(0);
  }
`;
